import { Checkbox, FormControlLabel, Grid } from "@mui/material";
import { FormikProps } from "formik";
import CustomTextArea from "@/components/CustomTextArea";
import Flex from "@/components/Flex";
import MaskMultiSelect from "@/components/MaskMultiSelect";
import Text from "@/components/Text";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import {
  CHART_COLORS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import useDisclosure from "@/hooks/use-disclosure";
import {
  GROUP_ACTIVITY_OPTIONS,
  GROUP_ACTIVITY_VISIBLE_TAGS,
  SESSION_NOTE_FIELD_LABEL,
  SESSION_NOTE_LABEL,
  SESSION_NOTE_PLACEHOLDER,
  SESSION_NOTE_TEXTAREA_ROWS,
  THERAPEUTIC_INTERVENTIONS_KEY,
  THERAPEUTIC_INTERVENTIONS_NOTE_KEY,
  THERAPEUTIC_INTERVENTION_SECTIONS,
} from "../group-visit-constant";
import {
  GroupActivityOption,
  SessionDocumentationHeader,
  SessionNoteValues,
} from "../group-visit-types";
import CheckboxOptionsModal from "./CheckboxOptionsModal";
import SelectedOptionsSummary from "./SelectedOptionsSummary";
import SelectedValuesList from "./SelectedValuesList";
import SessionInformationFields from "./SessionInformationFields";

export type SharedGroupNoteFormProps = {
  formik: FormikProps<SessionNoteValues>;
  /** The session being documented, for the read-only information block. */
  header: SessionDocumentationHeader;
  /** Attendees the note covers, for the caption under the title. */
  attendeeCount: number;
  disabled?: boolean;
};

type FreeTextFieldName =
  "sessionObjectives" | "topicsCovered" | "homework" | "overallGroupSummary";

const SECTION_TITLE_SX = {
  ...CHART_TYPOGRAPHY.body5,
  ...CHART_TYPOGRAPHY.overline,
  color: CHART_COLORS.btnSecondaryText,
};

/**
 * The one note that covers every attendee of the session.
 *
 * @author Rajkumar Rathod
 */
const SharedGroupNoteForm = ({
  formik,
  header,
  attendeeCount,
  disabled,
}: SharedGroupNoteFormProps) => {
  const { values, handleChange, handleBlur, setFieldValue } = formik;
  const interventionsDisclosure = useDisclosure();

  const interventionValues = {
    [THERAPEUTIC_INTERVENTIONS_KEY]: values.therapeuticInterventions,
    [THERAPEUTIC_INTERVENTIONS_NOTE_KEY]: values.therapeuticInterventionsNote,
  };

  const handleInterventionsChange = (next: Record<string, string>) => {
    setFieldValue(
      THERAPEUTIC_INTERVENTIONS_KEY,
      next[THERAPEUTIC_INTERVENTIONS_KEY] ?? ""
    );
    setFieldValue(
      THERAPEUTIC_INTERVENTIONS_NOTE_KEY,
      next[THERAPEUTIC_INTERVENTIONS_NOTE_KEY] ?? ""
    );
  };

  const renderFreeTextField = (name: FreeTextFieldName) => (
    <Flex flexDirection="column" gap={0.5}>
      <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.textMutedGrey}>
        {SESSION_NOTE_FIELD_LABEL[name]}
      </Text>
      <CustomTextArea
        name={name}
        value={values[name]}
        placeholder={SESSION_NOTE_PLACEHOLDER[name]}
        minRows={SESSION_NOTE_TEXTAREA_ROWS}
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    </Flex>
  );

  return (
    <Flex flexDirection="column" gap={2}>
      <Flex flexDirection="column" gap={0.25}>
        <Text
          component="h2"
          {...CHART_TYPOGRAPHY.body3}
          fontWeight={CHART_TYPOGRAPHY.weight.medium}
          c={CHART_COLORS.textDarker}
        >
          {SESSION_NOTE_LABEL.sharedNoteTitle}
        </Text>
        <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.textMutedGrey}>
          {`Applies to all ${attendeeCount} attendees · documented once`}
        </Text>
      </Flex>

      <Text sx={SECTION_TITLE_SX}>{SESSION_NOTE_LABEL.sessionInformation}</Text>

      <SessionInformationFields header={header} />

      <Grid container spacing={2}>
        <Grid size={12}>{renderFreeTextField("sessionObjectives")}</Grid>
      </Grid>

      <Text sx={SECTION_TITLE_SX}>
        {SESSION_NOTE_LABEL.clinicalDocumentation}
      </Text>

      <Grid container spacing={2}>
        <Grid size={12}>{renderFreeTextField("topicsCovered")}</Grid>

        <Grid size={12}>
          <Flex flexDirection="column" gap={0.5}>
            <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.textMutedGrey}>
              {SESSION_NOTE_FIELD_LABEL.therapeuticInterventions}
            </Text>
            {!disabled && (
              <StyledButtonV2
                label={SESSION_NOTE_LABEL.addTherapeuticInterventions}
                variant="outlined"
                shouldAddIcon
                fullWidth
                onClick={interventionsDisclosure.open}
              />
            )}
            <SelectedOptionsSummary
              sections={THERAPEUTIC_INTERVENTION_SECTIONS}
              values={interventionValues}
              onRemove={(field, value) => setFieldValue(field, value)}
              disabled={disabled}
            />
          </Flex>
        </Grid>

        <Grid size={12}>
          {disabled ? (
            <SelectedValuesList
              label={SESSION_NOTE_FIELD_LABEL.groupActivities}
              items={values.groupActivities}
            />
          ) : (
            <MaskMultiSelect<GroupActivityOption>
              label={SESSION_NOTE_FIELD_LABEL.groupActivities}
              placeholder={SESSION_NOTE_PLACEHOLDER.groupActivities}
              options={GROUP_ACTIVITY_OPTIONS}
              value={values.groupActivities}
              optionWithCheckbox
              limitTags={GROUP_ACTIVITY_VISIBLE_TAGS}
              onChange={(selected) =>
                setFieldValue("groupActivities", selected ?? [])
              }
            />
          )}
        </Grid>

        <Grid size={12}>
          <Flex flexDirection="column" justifyContent="center" height="100%">
            <FormControlLabel
              control={
                <Checkbox
                  name="isHomeworkAssigned"
                  checked={values.isHomeworkAssigned}
                  disabled={disabled}
                  onChange={(_, checked) => {
                    setFieldValue("isHomeworkAssigned", checked);
                    if (!checked) setFieldValue("homework", "");
                  }}
                />
              }
              label={
                <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.textDarker}>
                  {SESSION_NOTE_FIELD_LABEL.homeworkAssigned}
                </Text>
              }
            />
          </Flex>
        </Grid>

        {values.isHomeworkAssigned && (
          <Grid size={12}>{renderFreeTextField("homework")}</Grid>
        )}

        <Grid size={12}>{renderFreeTextField("overallGroupSummary")}</Grid>
      </Grid>

      {interventionsDisclosure.opened && (
        <CheckboxOptionsModal
          open={interventionsDisclosure.opened}
          onClose={interventionsDisclosure.close}
          title={SESSION_NOTE_LABEL.therapeuticInterventions}
          sections={THERAPEUTIC_INTERVENTION_SECTIONS}
          values={interventionValues}
          onSave={handleInterventionsChange}
          disabled={disabled}
        />
      )}
    </Flex>
  );
};

export default SharedGroupNoteForm;
